import { Component, inject, signal } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { forkJoin, timer } from 'rxjs';
import { SidebarComponent } from './core/components/sidebar/sidebar.component';
import { MobileNavComponent } from './core/components/mobile-nav/mobile-nav.component';
import { LoadingScreenComponent } from './core/components/loading-screen/loading-screen.component';
import { AuthService } from './core/services/auth.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    MobileNavComponent,
    LoadingScreenComponent
  ],
  template: `
    <div class="app-container" [class.login-screen]="!showShell">
      <!-- Loading screen overlay while profile is being fetched -->
      <app-loading-screen *ngIf="showShell && !profileResolved()"></app-loading-screen>

      <!-- App Shell: shown once profile is resolved (success or error) -->
      <ng-container *ngIf="!showShell || profileResolved()">
        <app-sidebar *ngIf="showShell"></app-sidebar>

        <div class="main-content">
          <router-outlet></router-outlet>
        </div>

        <app-mobile-nav *ngIf="showShell"></app-mobile-nav>
      </ng-container>
    </div>
  `,
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  protected readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  protected showShell = !this.router.url.startsWith('/login');

  /**
   * Tracks whether the profile fetch (+ minimum 3-second timer) has
   * completed.  Drives the visibility of <app-loading-screen>.
   */
  protected readonly profileResolved = signal<boolean>(
    !this.auth.isAuthenticated() || !!this.auth.currentUserProfile()
  );

  constructor() {
    if (this.auth.isAuthenticated() && !this.auth.currentUserProfile()) {
      this.startProfileFetch();
    }

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const isLogin = event.urlAfterRedirects.startsWith('/login');
        this.showShell = !isLogin;

        if (isLogin) {
          this.profileResolved.set(true);
        } else if (this.auth.isAuthenticated() && !this.auth.currentUserProfile()) {
          this.startProfileFetch();
        } else {
          this.profileResolved.set(true);
        }
      });
  }

  private startProfileFetch(): void {
    this.profileResolved.set(false);

    forkJoin([
      this.auth.getProfile(),
      timer(3000)
    ]).subscribe({
      next: () => this.profileResolved.set(true),
      error: (err) => {
        console.warn('Could not load profile in app navigation/init:', err);
        this.profileResolved.set(true);
        this.auth.clear();
        this.router.navigate(['/login']);
      }
    });
  }
}